import { useQuery } from "@tanstack/vue-query";
import { computed, type Ref, unref } from "vue";
import { HttpError, request } from "@/shared/http/client";

/**
 * A FONTE de uma view de componente, pelas duas portas que o servidor abre.
 *
 * `source` é a versão ATIVA — o que qualquer membro renderiza. `versions/:n` é
 * o preview de uma versão específica (rascunho incluído) e o servidor a guarda
 * com `@MinRole("admin")`; o front só a chama quando a URL pede
 * `?componentPreview=<n>` e a sessão pode — esconder é daqui, decidir é de lá.
 */
export interface ComponentSource {
	readonly version: number;
	readonly status: string;
	readonly source: string;
}

export const componentViewKeys = {
	source: (entity: string, view: string) =>
		["structure", "component", entity, view, "source"] as const,
	version: (entity: string, view: string, version: number) =>
		["structure", "component", entity, view, "version", version] as const,
};

/** O 404 que NÃO é avaria: ponteiro nulo = ninguém publicou ainda. */
function isNoActiveVersion(e: unknown): boolean {
	return (
		e instanceof HttpError &&
		e.kind === "notFound" &&
		(e.payload as { error?: string } | undefined)?.error === "NO_ACTIVE_VERSION"
	);
}

/**
 * A versão ativa — ou `null`, que é um ESTADO da tela ("nada publicado") e não
 * um erro. Sem essa distinção o EmptyState do host viraria um alerta vermelho
 * para toda entidade cujo componente ainda está em rascunho.
 */
export function useComponentSource(entity: Ref<string> | string, view: Ref<string | null>) {
	const code = computed(() => unref(entity));
	return useQuery({
		queryKey: computed(() => componentViewKeys.source(code.value, view.value ?? "")),
		queryFn: async (): Promise<ComponentSource | null> => {
			try {
				return await request<ComponentSource>(
					`/v3/structure/entities/${code.value}/views/${view.value}/component/source`,
				);
			} catch (e) {
				if (isNoActiveVersion(e)) return null;
				throw e;
			}
		},
		enabled: computed(() => code.value.length > 0 && view.value !== null),
		staleTime: 30_000,
	});
}

/** Uma versão ESPECÍFICA, para o preview de admin. Só roda com número na mão. */
export function useComponentVersion(
	entity: Ref<string> | string,
	view: Ref<string | null>,
	version: Ref<number | null>,
) {
	const code = computed(() => unref(entity));
	return useQuery({
		queryKey: computed(() =>
			componentViewKeys.version(code.value, view.value ?? "", version.value ?? 0),
		),
		queryFn: async (): Promise<ComponentSource> =>
			request(
				`/v3/structure/entities/${code.value}/views/${view.value}/component/versions/${version.value}`,
			),
		enabled: computed(() => code.value.length > 0 && view.value !== null && version.value !== null),
		staleTime: 30_000,
	});
}

/**
 * As telas do workspace, em UMA pergunta — o que o menu lateral precisa saber.
 *
 * Só as que têm versão ativa: uma tela em rascunho ainda não é um lugar, e uma
 * linha de menu que abre o vazio é pior do que linha nenhuma. O preview do
 * rascunho continua chegando pelo endereço, que o admin recebe do chat.
 */
export interface ComponentScreen {
	readonly entityCode: string;
	readonly entityName: string;
	readonly code: string;
	readonly name: string;
	readonly version: number;
	/** A tela que o documento marcou como a vista principal do dado: a linha do menu abre nela. */
	readonly isDefault?: boolean;
}

export function useComponentScreens(enabled: () => boolean = () => true) {
	return useQuery({
		enabled,
		queryKey: ["structure", "component", "screens"] as const,
		queryFn: async (): Promise<ComponentScreen[]> =>
			(await request<{ screens: ComponentScreen[] }>("/v3/structure/component-screens")).screens,
		staleTime: 60_000,
	});
}
